import type {
  ApprovalRequest,
  ArtifactRef,
  EffortLevel,
  ErrorCode,
  Id,
  ServerMessage,
  Task,
  TaskState,
} from "@uzverse/core";

export interface ChatItem {
  /** Message id (assistant) or client message id (user). */
  id: string;
  role: "user" | "assistant";
  text: string;
  taskId: Id | null;
  streaming: boolean;
}

export interface TaskItem {
  id: Id;
  /** Known for tasks loaded from history; live tasks use the user's primary company. */
  companyId?: Id;
  text: string;
  state: TaskState;
  detail?: string;
  /** The model and effort the run settled on, and which layer chose it. */
  model?: string;
  effort?: EffortLevel;
  modelSource?: string;
  artifacts: ArtifactRef[];
  createdAt: number;
  updatedAt: number;
}

export type ConnectionStatus = "connecting" | "online" | "offline";

export interface AppState {
  items: ChatItem[];
  tasks: Record<Id, TaskItem>;
  approvals: Record<Id, ApprovalRequest>;
  connection: ConnectionStatus;
  /** Last protocol error worth showing, cleared on the next successful action. */
  error: ErrorCode | null;
}

const EMPTY: AppState = {
  items: [],
  tasks: {},
  approvals: {},
  connection: "connecting",
  error: null,
};

/** Percent complete per task state, for the progress bar (not a promise about timing). */
export const TASK_PROGRESS: Record<TaskState, number> = {
  received: 5,
  routing: 15,
  planning: 30,
  in_progress: 55,
  review: 80,
  awaiting_approval: 65,
  done: 100,
  needs_owner: 100,
  cancelled: 100,
};

export const TERMINAL_STATES: readonly TaskState[] = ["done", "needs_owner", "cancelled"];

/** Client-side view of the realtime stream. Immutable updates, so `useSyncExternalStore` works. */
export class AppStore {
  private state: AppState = EMPTY;
  private readonly listeners = new Set<() => void>();
  /** clientMsgId -> text, until `chat.accepted` assigns a task. */
  private readonly pending = new Map<string, string>();

  getState = (): AppState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  setConnection(connection: ConnectionStatus): void {
    if (connection !== this.state.connection) this.set({ connection });
  }

  setError(error: ErrorCode | null): void {
    this.set({ error });
  }

  addUserMessage(clientMsgId: string, text: string): void {
    this.pending.set(clientMsgId, text);
    this.set({
      error: null,
      items: [
        ...this.state.items,
        { id: clientMsgId, role: "user", text, taskId: null, streaming: false },
      ],
    });
  }

  private upsertTask(taskId: Id, patch: Partial<TaskItem>): Record<Id, TaskItem> {
    const now = Date.now();
    const current = this.state.tasks[taskId] ?? {
      id: taskId,
      text: "",
      state: "received" as TaskState,
      artifacts: [],
      createdAt: now,
      updatedAt: now,
    };
    return { ...this.state.tasks, [taskId]: { ...current, ...patch, updatedAt: now } };
  }

  private upsertAssistant(
    messageId: string,
    taskId: Id,
    update: (prev: string) => string,
    streaming: boolean,
  ): ChatItem[] {
    const idx = this.state.items.findIndex((i) => i.role === "assistant" && i.id === messageId);
    if (idx === -1) {
      return [
        ...this.state.items,
        { id: messageId, role: "assistant", text: update(""), taskId, streaming },
      ];
    }
    return this.state.items.map((item, i) =>
      i === idx ? { ...item, text: update(item.text), streaming } : item,
    );
  }

  /**
   * Seeds the chat from the server's task list (newest first, as `GET .../tasks` returns it):
   * each task is the user's message plus the final reply. Tasks already known live are kept.
   */
  loadHistory(tasks: Task[]): void {
    const fresh = [...tasks].reverse().filter((t) => !this.state.tasks[t.id]);
    if (fresh.length === 0) return;
    const items: ChatItem[] = [];
    const known: Record<Id, TaskItem> = {};
    for (const t of fresh) {
      items.push({
        id: `${t.id}:user`,
        role: "user",
        text: t.text,
        taskId: t.id,
        streaming: false,
      });
      if (t.summary && TERMINAL_STATES.includes(t.state)) {
        items.push({
          id: `${t.id}:reply`,
          role: "assistant",
          text: t.summary,
          taskId: t.id,
          streaming: false,
        });
      }
      known[t.id] = {
        id: t.id,
        companyId: t.companyId,
        text: t.text,
        state: t.state,
        ...(t.diagnosis ? { detail: t.diagnosis } : {}),
        artifacts: t.artifacts,
        createdAt: Date.parse(t.createdAt),
        updatedAt: Date.parse(t.updatedAt),
      };
    }
    this.set({ items: [...items, ...this.state.items], tasks: { ...known, ...this.state.tasks } });
  }

  /** Applies one server message. Replays are idempotent: messages overwrite by id. */
  apply(message: ServerMessage): void {
    switch (message.type) {
      case "auth.ok":
        this.set({ connection: "online", error: null });
        return;
      case "chat.accepted": {
        const text = this.pending.get(message.clientMsgId) ?? "";
        this.pending.delete(message.clientMsgId);
        this.set({
          tasks: this.upsertTask(message.taskId, { text }),
          items: this.state.items.map((i) =>
            i.id === message.clientMsgId ? { ...i, taskId: message.taskId } : i,
          ),
        });
        return;
      }
      case "chat.delta":
        this.set({
          items: this.upsertAssistant(
            message.messageId,
            message.taskId,
            (prev) => prev + message.delta,
            true,
          ),
        });
        return;
      case "chat.message":
        this.set({
          items: this.upsertAssistant(message.messageId, message.taskId, () => message.text, false),
        });
        return;
      case "task.status": {
        const finished = TERMINAL_STATES.includes(message.state);
        this.set({
          tasks: this.upsertTask(message.taskId, {
            state: message.state,
            ...(message.detail ? { detail: message.detail } : {}),
          }),
          items: finished
            ? this.state.items.map((i) =>
                i.taskId === message.taskId && i.streaming ? { ...i, streaming: false } : i,
              )
            : this.state.items,
        });
        return;
      }
      case "task.artifact": {
        const task = this.state.tasks[message.taskId];
        const known = task?.artifacts.some((a) => a.path === message.artifact.path);
        if (known) return;
        this.set({
          tasks: this.upsertTask(message.taskId, {
            artifacts: [...(task?.artifacts ?? []), message.artifact],
          }),
        });
        return;
      }
      case "task.model":
        this.set({
          tasks: this.upsertTask(message.taskId, {
            model: message.model,
            ...(message.effort ? { effort: message.effort } : {}),
            ...(message.source ? { modelSource: message.source } : {}),
          }),
        });
        return;
      case "approval.request":
        this.set({
          approvals: { ...this.state.approvals, [message.approval.id]: message.approval },
        });
        return;
      case "approval.resolved": {
        const current = this.state.approvals[message.approvalId];
        if (!current) return;
        this.set({
          approvals: {
            ...this.state.approvals,
            [message.approvalId]: { ...current, status: message.status },
          },
        });
        return;
      }
      case "error":
        if (message.code === "unauthenticated") this.set({ error: message.code });
        return;
      case "entity.changed":
      case "pong":
        return;
    }
  }
}
